import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import ReactPaginate from "react-paginate";
import { postData } from "../../../utility/Utility";
import Swal from "sweetalert2";
import CustomLoader from "../../../Components/CustomLoader/CustomLoader";

function DesignationList() {
  const navigate = useNavigate();
  const [designations, setDesignations] = useState([]);
  const [loading, setLoading] = useState(false);
  const [searchTerm, setSearchTerm] = useState("");
  const [currentPage, setCurrentPage] = useState(0);
  const [totalPages, setTotalPages] = useState(0);
  const rowsPerPage = 10;

  // Sorting state
  const [sortField, setSortField] = useState("createdAt");
  const [sortOrder, setSortOrder] = useState("asc");

  // Filter state
  const [filters, setFilters] = useState({
    targetRole: "",
    count: ""
  });

  const fetchDesignations = async (page = 1) => {
    setLoading(true);
    try {
      const payload = {
        page: page,
        limit: rowsPerPage,
        search: searchTerm || "",
        sort: {
          field: sortField,
          order: sortOrder
        },
        filters: Object.entries(filters)
          .filter(([, value]) => value !== "")
          .map(([key, value]) => ({ key, value }))
      };
      const response = await postData("/api/promotionCtrl/list", payload);
      if (response?.success) {
        setDesignations(response.data || []);
        setTotalPages(response.pagination?.totalPages || 0);
      }
    } catch (error) {
      console.error("Fetch designations error:", error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchDesignations(currentPage + 1);
    // eslint-disable-next-line
  }, [currentPage]);

  const handlePageChange = ({ selected }) => {
    setCurrentPage(selected);
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    setCurrentPage(0);
    fetchDesignations(1);
  };

  const handleReset = async () => {
    // Reset all state
    setSearchTerm("");
    setCurrentPage(0);
    setSortField("createdAt");
    setSortOrder("asc");
    setFilters({ targetRole: "", count: "" });
    
    // Fetch with reset parameters directly
    setLoading(true);
    try {
      const payload = {
        page: 1,
        limit: rowsPerPage,
        search: "",
        sort: {
          field: "createdAt",
          order: "asc"
        },
        filters: []
      };
      const response = await postData("/api/promotionCtrl/list", payload);
      if (response?.success) {
        setDesignations(response.data || []);
        setTotalPages(response.pagination?.totalPages || 0);
      }
    } catch (error) {
      console.error("Fetch designations error:", error);
    } finally {
      setLoading(false);
    }
  };

  // Handle sorting
  const handleSort = (field) => {
    if (sortField === field) {
      setSortOrder(sortOrder === "asc" ? "desc" : "asc");
    } else {
      setSortField(field);
      setSortOrder("asc");
    }
    setCurrentPage(0);
    fetchDesignations(1);
  };

  // Handle filter changes
  const handleFilterChange = (filterKey, value) => {
    setFilters(prev => ({
      ...prev,
      [filterKey]: value
    }));
  };

  // Get sort icon
  const getSortIcon = (field) => {
    if (sortField !== field) return <i className="mdi mdi-sort"></i>;
    return sortOrder === "asc" ? 
      <i className="mdi mdi-sort-ascending"></i> : 
      <i className="mdi mdi-sort-descending"></i>;
  };

  // ✅ Delete Designation
  const handleDeleteDesignation = async (designationId) => {
    const result = await Swal.fire({
      title: "Are you sure?",
      text: "This will permanently delete the designation.",
      icon: "warning",
      showCancelButton: true,
      confirmButtonColor: "#3085d6",
      cancelButtonColor: "#d33",
      confirmButtonText: "Yes, delete it!",
    });

    if (result.isConfirmed) {
      try {
        const response = await postData("/api/promotionCtrl/delete", {
          id: designationId,
        });

        if (response?.success) {
          Swal.fire("Deleted!", "Designation has been deleted.", "success");
          fetchDesignations(currentPage + 1);
        } else {
          Swal.fire(
            "Error!",
            response?.message || "Failed to delete designation.",
            "error"
          );
        }
      } catch (error) {
        console.error("Delete error:", error);
        Swal.fire("Error!", "Something went wrong while deleting.", "error");
      }
    }
  };

  return (
    <div className="content-page">
      <CustomLoader loading={loading} />
      <div className="content">
        <div className="container-fluid">
          <div className="py-3 d-flex align-items-sm-center flex-sm-row flex-column justify-content-between">
            <div className="flex-grow-1">
              <h4 className="fs-18 fw-semibold m-0">Designation Management</h4>
              <p className="fw-semibold my-2">Designation List</p>
            </div>
            <div>
              <button
                className="btn btn-primary"
                onClick={() => navigate("/designation-add")}
                disabled={loading}
              >
                {loading ? "Adding..." : "Add Designation"}
              </button>
            </div>
          </div>

          {/* Search */}
          <div className="row">
            <div className="col-12">
              <div className="card">
                <div className="card-body">
                  <form onSubmit={handleSubmit}>
                    <div className="row g-3 align-items-end">
                      <div className="col-md-3">
                        <label htmlFor="searchTerm" className="form-label">
                          Search
                        </label>
                        <input
                          type="text"
                          className="form-control"
                          placeholder="Search current or target designation..."
                          value={searchTerm}
                          onChange={(e) => setSearchTerm(e.target.value)}
                        />
                      </div>
                      <div className="col-md-3 d-flex gap-2">
                        <button
                          type="submit"
                          className="btn btn-primary"
                          disabled={loading}
                        >
                          {loading ? "Searching..." : "Search"}
                        </button>
                        <button
                          type="button"
                          className="btn btn-secondary"
                          onClick={handleReset}
                          disabled={loading}
                        >
                          Reset
                        </button>
                      </div>
                    </div>
                  </form>
                </div>
              </div>
            </div>
          </div>

          {/* Promotion Criteria Table */}
          <div className="row mt-2">
            <div className="col-12">
              <div className="card">
                <div className="card-body">
                  {loading ? (
                    <div className="text-center py-5">Loading promotion criteria...</div>
                  ) : (
                    <div className="table-responsive">
                      <table className="table table-hover">
                        <thead>
                          <tr>
                            <th>S.No</th>
                            <th 
                              className="cursor-pointer user-select-none"
                              onClick={() => handleSort('currentRole')}
                              title="Click to sort"
                            >
                              Current Role {getSortIcon('currentRole')}
                            </th>
                            <th 
                              className="cursor-pointer user-select-none"
                              onClick={() => handleSort('targetRole')}
                              title="Click to sort"
                            >
                              Target Role {getSortIcon('targetRole')}
                            </th>
                            <th 
                              className="cursor-pointer user-select-none"
                              onClick={() => handleSort('count')}
                              title="Click to sort"
                            >
                              Required Agent {getSortIcon('count')}
                            </th>
                            <th>Child Role Name</th>
                            <th 
                              className="cursor-pointer user-select-none"
                              onClick={() => handleSort('createdAt')}
                              title="Click to sort"
                            >
                              Created At {getSortIcon('createdAt')}
                            </th>
                            <th width="150px">Actions</th>
                          </tr>
                        </thead>
                        <tbody>
                          {designations.length > 0 ? (
                            designations.map((designation, index) => (
                              <tr key={designation._id}>
                                <td>{currentPage * rowsPerPage + index + 1}</td>
                                <td>{designation.currentRole}</td>
                                <td>{designation.targetRole}</td>
                                <td>{designation.count}</td>
                                <td>{designation.childRoleToCount}</td>
                                <td>
                                  {designation.createdAt
                                    ? new Date(
                                        designation.createdAt
                                      ).toLocaleDateString()
                                    : ""}
                                </td>
                                <td>
                                  <div className="d-flex gap-2">
                                    {/* Edit */}
                                    <button
                                      className="btn btn-sm btn-outline-info"
                                      onClick={() =>
                                        navigate("/designation-add", {
                                          state: { designation },
                                        })
                                      }
                                      title="Edit"
                                    >
                                      <i className="mdi mdi-pencil"></i>
                                    </button>

                                    {/* Delete */}
                                    <button
                                      className="btn btn-sm btn-outline-danger"
                                      onClick={() =>
                                        handleDeleteDesignation(designation._id)
                                      }
                                      title="Delete"
                                    >
                                      <i className="mdi mdi-delete"></i>
                                    </button>
                                  </div>
                                </td>
                              </tr>
                            ))
                          ) : (
                            <tr>
                              <td colSpan="7" className="text-center py-4">
                                No promotion criteria found
                              </td>
                            </tr>
                          )}
                        </tbody>
                      </table>

                      {totalPages > 1 && (
                        <ReactPaginate
                          previousLabel={
                            <i className="mdi mdi-chevron-left"></i>
                          }
                          nextLabel={<i className="mdi mdi-chevron-right"></i>}
                          breakLabel="..."
                          breakClassName="page-item"
                          breakLinkClassName="page-link"
                          pageCount={totalPages}
                          marginPagesDisplayed={2}
                          pageRangeDisplayed={5}
                          onPageChange={handlePageChange}
                          containerClassName="pagination pagination-rounded justify-content-end mt-3"
                          pageClassName="page-item"
                          pageLinkClassName="page-link"
                          previousClassName="page-item"
                          previousLinkClassName="page-link"
                          nextClassName="page-item"
                          nextLinkClassName="page-link"
                          activeClassName="active"
                          forcePage={currentPage}
                        />
                      )}
                    </div>
                  )}
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

export default DesignationList;
